// Stream URL resolver. Live playback resolution is owned by
// POST /api/playback/live-session so proxy / rewind / transcode / remux /
// catchup precedence is decided once on the backend. The composable keeps the
// legacy `{ url, mode, probeInfo, cleanup }` contract for the player store.

import {
    api,
    type LivePlaybackForceMode,
    type LivePlaybackMode,
    type LivePlaybackSessionRequest,
    type ProbeInfo,
    type SubtitleFormat,
} from '@/lib/api';
import { log } from '@/lib/log';

export interface PlayableChannel {
    sourceId: number;
    /** Stream / item id — what the legacy SPA calls `channel.id`. */
    id: string;
    /** Container hint; defaults to `'m3u8'`. Some M3U providers want `'ts'`. */
    streamFormat?: string;
}

export interface CatchupRequest {
    /** Catchup window start in epoch milliseconds. */
    startMs: number;
    /** Catchup window duration in minutes. */
    durationMin: number;
}

export interface UseStreamUrlOptions {
    /** When present, catchup wins over rewind / transcode / proxy. */
    catchup?: CatchupRequest;
    /** Cancel an in-flight playback-session request on channel switch. */
    signal?: AbortSignal;
    /**
     * Dumb retry override after a playback failure. The backend picks the
     * remedy (buffer audio-repair or transcode session) — no codec fields.
     */
    forceMode?: LivePlaybackForceMode;
    /** Per-tune diagnostic correlation id. Non-secret; appended only to local NodeCast stream URLs. */
    playTraceId?: string | null;
    /** Admin-only explicit slot takeover (re-request after a `canTakeover` busy 409). */
    takeover?: boolean;
    /**
     * live-buffer-resilience — explicit same-user slot-conflict consent.
     * See `LivePlaybackSessionRequest.replacePlaybackId`.
     */
    replacePlaybackId?: string | string[];
    /**
     * X2 (ux-remediation D3) — explicit user Retry bypasses the server's
     * probe/preflight cache. Set ONLY by user-initiated retry paths (busy/
     * offline toast actions); a first tune-in omits it so first-attempt
     * behavior is completely unchanged.
     */
    fresh?: boolean;
}

export type StreamMode = LivePlaybackMode;

export interface StreamUrlResult {
    url: string;
    mode: StreamMode;
    /**
     * Rewind sessions carry the backend's LOCAL buffer probe (what the
     * browser will actually download); other live modes may be null, in
     * which case `NodecastPlayer` runs a stats-display-only probe.
     */
    probeInfo: ProbeInfo | null;
    /** Unified backend cleanup endpoint. Optional for older tests / VOD results. */
    cleanupUrl?: string;
    /**
     * The playback registry id for this session. Was previously only closed
     * over internally by the `cleanup` closure; lifted out (live-buffer-
     * resilience) so callers can pass it as a future tune's
     * `replacePlaybackId` (self-steal on the next tune / retry) without a
     * second round trip.
     */
    playbackId?: string;
    /** Rewind only: explicit playlist duration reported by the backend when known. */
    availableDurationSeconds?: number | null;
    /** Rewind only: shared buffer creation time, used to infer join position. */
    bufferStartedAtMs?: number | null;
    /** Rewind only: retention cap for the shared playlist window. */
    retentionSeconds?: number | null;
    /**
     * The CHANNEL's own track counts (live-audio-track-selection Phase 4).
     * Resolved here so consumers never have to know that only the rewind
     * path needs the fallback: `probeInfo` on that path describes the
     * single-audio LOCAL buffer, while every other live mode's probeInfo is
     * the upstream itself.
     */
    trackCounts?: {
        audio: number | null;
        subtitle: number | null;
        subtitleRenderable: number | null;
        subtitleFormat: SubtitleFormat;
        subtitleExternal: boolean;
    };
    /** Rewind only: elapsed buffer age when reported by older backend paths. */
    elapsedSeconds?: number | null;
    /**
     * live-buffer-resilience — epoch-based sink-restart continuity state.
     * Populated for `mode: 'rewind'`; null for every other live mode.
     */
    bufferState?: 'live' | 'reconnecting' | null;
    degradedReason?: string | null;
    /** Severity, orthogonal to `degradedReason`. See api.ts. */
    degradedUnstable?: boolean;
    lastSegmentAtMs?: number | null;
    bufferDepthSeconds?: number | null;
    /** Releases server-side resources. Idempotent. */
    cleanup: () => Promise<void>;
}

/**
 * The CHANNEL's own audio/subtitle track counts for one live session.
 *
 * The mode guard is the whole point. On `rewind`, `probeInfo` describes the
 * LOCAL buffer output, which `analyzeProbeResult` always reports as exactly
 * one audio track and zero subtitles because the sink maps `-map 0:a:0?` —
 * so falling back to it would not be "less precise", it would record a
 * FABRICATED 1 over a previously measured 4 and drop the channel out of the
 * N×audio chip and the multi-audio facet (pre-commit review 2026-08-25,
 * finding #1). That path is reachable whenever the upstream probe threw or
 * was skipped (audio-repair re-tune), so on rewind the honest answer for an
 * absent `upstreamTracks` is "unknown", never the local count. Every other
 * live mode probes upstream directly, so there `probeInfo` IS the channel.
 */
export function resolveTrackCounts(session: {
    mode: string;
    upstreamTracks?: {
        audio: number | null;
        subtitle: number | null;
        subtitleRenderable?: number | null;
        subtitleFormat?: SubtitleFormat;
        subtitleExternal?: boolean;
    } | null;
    probeInfo?: ProbeInfo | null;
}): {
    audio: number | null;
    subtitle: number | null;
    subtitleRenderable: number | null;
    subtitleFormat: SubtitleFormat;
    subtitleExternal: boolean;
} {
    if (session.mode === 'rewind') {
        return {
            audio: session.upstreamTracks?.audio ?? null,
            subtitle: session.upstreamTracks?.subtitle ?? null,
            subtitleRenderable: session.upstreamTracks?.subtitleRenderable ?? null,
            subtitleFormat: session.upstreamTracks?.subtitleFormat ?? null,
            subtitleExternal: session.upstreamTracks?.subtitleExternal === true,
        };
    }
    const probe = session.probeInfo;
    return {
        audio: typeof probe?.audioTracks === 'number' ? probe.audioTracks : null,
        subtitle: typeof probe?.subtitleTracks === 'number' ? probe.subtitleTracks : null,
        // live-subtitle-tracks §A2 — same mode guard, same reason: an absent
        // count is "unknown", never a fabricated 0. A 0 here means "measured,
        // and none of them are renderable"; null means "we did not measure".
        subtitleRenderable:
            typeof probe?.subtitleRenderableTracks === 'number'
                ? probe.subtitleRenderableTracks
                : null,
        subtitleFormat: probe?.subtitleFormat ?? null,
        // Only the shared-sink path emits the external feed; every other
        // live mode has no sink to emit it from.
        subtitleExternal: false,
    };
}

export async function useStreamUrl(
    channel: PlayableChannel,
    opts: UseStreamUrlOptions,
): Promise<StreamUrlResult> {
    const payload: LivePlaybackSessionRequest = {
        sourceId: channel.sourceId,
        channelId: channel.id,
        streamFormat: channel.streamFormat ?? 'm3u8',
        catchup: opts.catchup,
        playTraceId: opts.playTraceId ?? null,
        playbackAttemptId: opts.playTraceId ?? null,
        // log.sessionId is tab-scoped and survives reload via sessionStorage.
        // That is the exact boundary the server needs: recover this tab's
        // abandoned browser playback without touching another tab or device.
        clientSessionId: log.sessionId,
    };
    if (opts.forceMode) payload.forceMode = opts.forceMode;
    if (opts.takeover) payload.takeover = true;
    if (opts.replacePlaybackId) payload.replacePlaybackId = opts.replacePlaybackId;
    if (opts.fresh) payload.fresh = true;

    const session = await api.playback.startLiveSession(payload, opts.signal);
    const trackCounts = resolveTrackCounts(session);

    log.debug('streamUrl.resolved', {
        mode: session.mode,
        sourceId: channel.sourceId,
        playTraceId: opts.playTraceId ?? null,
        playbackId: session.playbackId,
        sinkIdPresent: Boolean(session.sinkId),
        elapsedSeconds: session.elapsedSeconds ?? null,
        readyReason: session.readyReason ?? null,
        initialSegments: session.initialSegments ?? null,
        availableDurationSeconds: session.availableDurationSeconds ?? null,
        shared: session.shared === true,
        retentionSeconds: session.retentionSeconds ?? null,
        trackCounts,
        activeViewers: session.activeViewers ?? null,
        bufferStartedAtMs: session.bufferStartedAtMs ?? null,
        bufferState: session.bufferState ?? null,
    });

    return {
        url: session.url,
        mode: session.mode,
        probeInfo: session.probeInfo ?? null,
        cleanupUrl: session.cleanupUrl,
        playbackId: session.playbackId,
        availableDurationSeconds: session.availableDurationSeconds ?? null,
        bufferStartedAtMs: session.bufferStartedAtMs ?? null,
        retentionSeconds: session.retentionSeconds ?? null,
        trackCounts,
        elapsedSeconds: session.elapsedSeconds ?? null,
        bufferState: session.bufferState ?? null,
        degradedReason: session.degradedReason ?? null,
        degradedUnstable: session.degradedUnstable === true,
        lastSegmentAtMs: session.lastSegmentAtMs ?? null,
        bufferDepthSeconds: session.bufferDepthSeconds ?? null,
        cleanup: (() => {
            let pending: Promise<void> | null = null;
            return () => {
                // Both the store and media watcher release this result. Every
                // caller must await the actual DELETE, including a second call
                // while the first is still closing the provider connection.
                pending ??= (async () => {
                    try {
                        await api.playback.endLiveSession(session.playbackId);
                    } catch (err) {
                        log.debug('streamUrl.cleanup_failed', {
                            mode: session.mode,
                            error: (err as Error).message,
                        });
                    }
                })();
                return pending;
            };
        })(),
    };
}
